import { useState, useEffect } from "react";
import { Task, TaskFormData, TaskType } from "@/types/task";
import { useTasks, useTaskCategories } from "@/hooks/useTasks";
import { useEmployees } from "@/hooks/useEmployees";
import { useClients } from "@/hooks/useClients";
import { useCompanies } from "@/hooks/useCompanies";
import { useSuppliers } from "@/hooks/useSuppliers";
import { toast } from "sonner";

const emptyFormData: TaskFormData = {
  title: "",
  description: "",
  category_id: "",
  priority: "medium",
  type: "general",
  client_id: "",
  company_id: "",
  supplier_id: "",
  assigned_to: "",
  due_date: "",
  estimated_hours: undefined,
  tags: [],
};

export const useTaskForm = (
    task: Task | null | undefined,
    onSuccess: (() => void) | undefined,
    onOpenChange: (open: boolean) => void,
    taskTypes: TaskType[],
    open: boolean
) => {
    const [customType, setCustomType] = useState("");
    const [formData, setFormData] = useState<TaskFormData>(emptyFormData);

    const { createTask, updateTask, deleteTask, isCreating, isUpdating } = useTasks();
    const { categories } = useTaskCategories();
    const { employees } = useEmployees();
    const { data: clients = [] } = useClients();
    const { data: companies = [] } = useCompanies();
    const { data: suppliers = [] } = useSuppliers();

    useEffect(() => {
        if (open) {
          if (task) {
            const predefinedTypes = taskTypes.map(t => t.id);
            const isCustomType = task.type && !predefinedTypes.includes(task.type);
    
            setFormData({
              title: task.title || "",
              description: task.description || "",
              category_id: task.category_id || "",
              priority: task.priority || "medium",
              type: isCustomType ? 'custom' : task.type,
              client_id: task.client_id || "",
              company_id: task.company_id || "",
              supplier_id: task.supplier_id || "",
              assigned_to: task.assigned_to || "",
              due_date: task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : "",
              estimated_hours: task.estimated_hours || undefined,
              tags: task.tags || [],
            });
    
            if (isCustomType) {
              setCustomType(task.type);
            } else {
              setCustomType("");
            }
          } else {
            setFormData(emptyFormData);
            setCustomType("");
          }
        }
      }, [open, task, taskTypes]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.title.trim()) {
          toast.error("عنوان المهمة مطلوب");
          return;
        }
        
        try {
          const finalType = formData.type === 'custom' ? customType : formData.type;
          
          if (formData.type === 'custom' && !customType.trim()) {
            toast.error("يرجى إدخال نوع المهمة المخصص");
            return;
          }

          const emptyToUndefined = (value: string | undefined) => (value === 'null' || !value) ? undefined : value;
          
          const finalFormData = { 
            ...formData, 
            type: finalType,
            category_id: emptyToUndefined(formData.category_id),
            client_id: emptyToUndefined(formData.client_id),
            company_id: emptyToUndefined(formData.company_id),
            supplier_id: emptyToUndefined(formData.supplier_id),
            assigned_to: emptyToUndefined(formData.assigned_to),
          };
          
          let success = false;
          if (task) {
            success = await updateTask(task.id, finalFormData);
            if (success) toast.success("تم تحديث المهمة بنجاح");
          } else {
            success = await createTask(finalFormData);
            if (success) toast.success("تم إضافة المهمة بنجاح");
          }
          
          if (success) {
            onOpenChange(false);
            onSuccess?.();
          }
        } catch (error) {
          console.error('Task submission error:', error);
          toast.error("حدث خطأ أثناء حفظ المهمة");
        }
      };
    
      const handleDelete = async () => {
        if (!task) return;
        
        if (!confirm('هل أنت متأكد من حذف هذه المهمة؟')) return;
        
        try {
          await deleteTask(task.id);
          toast.success("تم حذف المهمة بنجاح");
          onOpenChange(false);
          onSuccess?.();
        } catch (error) {
          console.error('Task deletion error:', error);
          toast.error("حدث خطأ أثناء حذف المهمة");
        }
      };

    return {
        formData,
        setFormData,
        customType,
        setCustomType,
        handleSubmit,
        handleDelete,
        isSubmitting: isCreating || isUpdating,
        categories,
        employees,
        clients,
        companies,
        suppliers
    };
};
